import { Logger } from "~/modules/logger";
import { generateBackoffInterval, isValidNumber } from "~/utils/retry/retryUtil";

export interface RetryOptions<T> {
    functionToRetry: () => Promise<T>;

    retryCondition: (error?: object) => boolean;

    logger: Logger;

    initialDelay?: number;

    maxDelay?: number;

    maxAttempts?: number;
}

interface ValidatedRetryParams {
    initialDelay: number;
    maxDelay: number;
    maxAttempts: number;
}

function validateAndNormalizeParams<T>(options: RetryOptions<T>): ValidatedRetryParams {
    const logger = options.logger;
    let initialDelay = 800;
    let maxDelay = 3 * 1000; 
    let maxAttempts = 3;

    if (isValidNumber(options.initialDelay)) {
        initialDelay = options.initialDelay!;
    }
    if (isValidNumber(options.maxDelay)) {
        maxDelay = options.maxDelay!;
    }
    if (isValidNumber(options.maxAttempts)) {
        maxAttempts = options.maxAttempts!;
    }

    if (initialDelay > maxDelay) {
        logger.warn("Initial delay cannot be greater than Max delay, hence defaulting to Max delay");
    }
    if (maxAttempts < 1) {
        logger.warn("The value of max retry attempts has to be greater than 0, hence reset to 1");
        maxAttempts = 1;
    }

    return { initialDelay, maxDelay, maxAttempts };
}

async function executeRetryAttempt<T>(
    options: RetryOptions<T>,
    retryCount: number
): Promise<{ success: true; result: T } | { success: false; error: Error; shouldRetry: boolean }> {
    const logger = options.logger;

    try {
        if (retryCount > 0) {
            logger.debug("Retry attempt: ", retryCount);
        }

        const result = await options.functionToRetry();
        return { success: true, result };
    } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        const shouldRetry = options.retryCondition(error as object);
        return { success: false, error: errorObj, shouldRetry };
    }
}

export async function retry<T>(options: RetryOptions<T>): Promise<T> {
    const logger = options.logger;
    const { initialDelay, maxDelay, maxAttempts } = validateAndNormalizeParams(options);
    let retryCount = 0;

    let errorToReturn: Error | undefined;
    do {
        const attemptResult = await executeRetryAttempt(options, retryCount);

        if (attemptResult.success) {
            return attemptResult.result;
        }

        if (!attemptResult.shouldRetry) {
            return Promise.reject(attemptResult.error);
        }

        if (retryCount >= maxAttempts - 1) {
            errorToReturn = attemptResult.error;
            break;
        }

        const delay = generateBackoffInterval(initialDelay, maxDelay, retryCount + 1);
        logger.debug("Retry after(ms): ", delay);
        await new Promise((resolve) => setTimeout(resolve, delay));
    } while (retryCount++ < maxAttempts - 1);

    return Promise.reject(errorToReturn ?? new Error("Retry failed"));
}
